import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import Card from '../shared/cards/Card';
import { dynamicFormData, NewOrganizationFormLayout } from '../../util/constants';
import MenuButtonsPortal from '../Menu/MenuButtonsPortal';
import BiButton from '../primitives/buttons/BiButton.primitive';
import DynamicFormFieldRenderer from '../Reusable/DynamicFormFieldRenderer';
import {
  fieldIsEmpty,
  validateAddress,
  validateContact,
  validateLength,
  validateName,
  validatePoastalCode
} from '../../util/validators';
import {
  createOrganization,
  deleteOrganization,
  getOrganization,
  patchOrganization
} from '../../http/organization-services';
import ToastNotification from '../Reusable/ToastNotification';
import LoaderContainer from '../shared/loaderContainer/LoaderContainer';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { setURLPaths } from '../../app/organizationReducer';
import { library, SettingIcon } from '../../assets/components/svgs';
import { HTTP_STATUS } from '../../http/constants/http.status';
import {
  createBucket,
  createSignedURL,
  deleteBucket,
  uploadFile
} from '../../http/asset-management-services';
import {
  NotificationTemplate,
  createNotificationTemplate,
  getNotificationTemplatesForOrg,
  getNotificationTemplates
} from '../../http/notification-services';
import { setToastData } from '../../app/toastReducer';
import { getOrganizationDetails } from './helpers';
import {
  getLoggedInOrgName,
  userFilterQuery,
  getLoggedInOrg,
  getLoginUserRoles,
  switchSession
} from '../../util/admin-utils';
import { getUsers, enrollUser } from '../../http/user-management-services';
import { AppConstants } from '../../constants/AppConstants';
import { Category, SystemSetting } from '../../util/RoleUtil';
import {
  addCategoryToOrganization,
  createOrganizationSetting,
  getOrganizationCategories,
  getOrganizationSettings,
  getCategories,
  getSystemSettings
} from '../../http/configuration-services';
import {
  isEqual,
  compact,
  isEmpty,
  find,
  intersection,
  reduce,
  differenceWith,
  uniq,
  uniqBy
} from 'lodash';
import { getBucketName } from '../../util/AppSetting';
import TaskTracker from '../shared/taskTracker/TaskTracker';
import { TaskTrackerConstants } from '../../constants/TaskTrackerConstants';
import BrowserEventHandler from '../shared/browserEventHandler/browserEventHandler';
import { getFailureMessage } from '../../util/ErrorUtil';
import { useTheme } from '../../context/themeContext';
import { useTranslation } from 'react-i18next';
import Checkbox from '../shared/Fields/Checkbox';
import { useQuery } from '../../hooks/queryParams';
import { formatPhoneNumber, getCountryCodeAndNumber } from '../../util/functions';
import {
  customOrgTypeCoding,
  getOrganizationType,
  parseTemplateLiteral
} from '../../util/OrganizationUtil';
import { RouteConstants } from '../../constants/RouteConstants';

export interface NewOrganization {
  name: string;
  phone: string;
  fax: string;
  address: string;
  city: string;
  zip: string;
  state: string;
  country: string;
  type: string;
  countryCode?: string;
  faxCountryCode?: string;
}

interface CreateOrganizationProps {
  isNewChildOrganization?: boolean;
  parentOrganizationId?: string;
}
/**
 * @parentId organizations
 * @manager Create, View, Edit - Organization
 * @overview Create, View, Edit of Organization
 *   <section>
 *       <p>
 *           <b>View Organization</b><br/>
 *           Clicking on organization row on the organization listing page will take user to view organization page in read-only mode. Clicking on <b>Back to organization list</b> will take the user to organization listing page.
 *           <br/><br/>
 *           <b>Edit Organization</b>
 *           <br/>
 *           Editing of organization is only possible if the logged in user role has editing API permissions. i.e <strong>organization-service.organization.update or organization-service.organization.patch</strong>
 *           Clicking on <b>Edit Organization Details</b> menu from <b>Manage Organization</b> drop down menu list of admin portal toolbar will change to editing mode, which allows users to edit all displayed fields of organization entity. Clicking on <b>Cancel</b> will take the user to organization details page in read-only mode.
 *           <b>Note:</b> Type field of organization will be disabled for edit based on certain system settings which will be descibed below.
 *           <br/><br/>
 *           <b>Create Organization</b><br/>
 *           Create <b>New Organization</b> button is only enabled if the logged-in user role API permission has create organization permission. i.e <strong>organization-service.organization.create</strong>
 *           Clicking on <b>New Organization</b> button on organization listing page will take user to New Organization creation page. Clicking on <b>Cancel</b> will take the user to organization listing page.
 *      
 *           <br/>
 *           User can create new organization by entering <b>Name</b> mandatory field and clicking on <b> Save </b> button on the admin portal toolbar.
 *       </p>
 *   </section>
 *   <section>
 *          <h4>View/Edit Metadata Application Dropdown Menu</h4>
 *          Following are the menu items listed under metadata application <b>Manage Metadata</b> drop down.
 *          <br>
 *          <p>
 *          User can perform a set of actions on each organization based on his role. Following are the actions which are displayed in the Context Menu for each organization based on the role API permissions. Few of them might be hidden due to logged in user role not having required API permissions.
 *          <br/>
 *          <table>
 *           <tr>
 *               <th>Action</th>
 *               <th>Role API Permission</th>
 *           </tr>
 *           <tr>
 *               <td>Edit Organization Details</td>
 *               <td>organization-service.organization.update</td>
 *           </tr>
 *           <tr>
 *               <td>Invite Users</td>
 *               <td>N/A</td>
 *           </tr>
 *           <tr>
 *               <td>View Users</td>
 *               <td>user-management-service.user.list</td>
 *           </tr>
 *           <tr>
 *               <td>View Templates</td>
 *               <td>notification-service.template.list</td>
 *           </tr>
 *           <tr>
 *               <td>Manage Contracts</td>
 *               <td>asset-management-service.bucket.files</td>
 *           </tr>
 *           <tr>
 *               <td>View Settings</td>
 *               <td>configuration-service.organization-setting.list</td>
 *           </tr>
 *           <tr>
 *               <td>View Categories</td>
 *               <td>configuration-service.organization-category.list</td>
 *           </tr>
 *          </table>
 *       </p>
 *   </section>
 *   <section>

 *          <h4>View/Create/Edit Organization Fields</h4>
 *          Create/View/Edit Organization page will display below fields with following validation constraints.
 *       <p>
 *          <table>
 *            <tr>
 *               <th>Field Name</th>
 *               <th>Required</th>
 *               <th>Data Type</th>
 *               <th>Max Length</th>
 *            </tr>
 *            <tr>
 *               <td>Name</td>
 *               <td>Yes</td>
 *               <td>String</td>
 *               <td>255</td>
 *            </tr>
 *            <tr>
 *               <td>Active</td>
 *               <td>Yes</td>
 *               <td>Boolean</td>
 *               <td>N/A</td>
 *            </tr>
 *            <tr>
 *               <td>Type</td>
 *               <td>No</td>
 *               <td>String</td>
 *               <td>255</td>
 *            </tr>
 *            <tr>
 *               <td>Address</td>
 *               <td>No</td>
 *               <td>String</td>
 *               <td>255</td>
 *            </tr>
 *            <tr>
 *               <td>Country</td>
 *               <td>No</td>
 *               <td>String</td>
 *               <td>255</td>
 *            </tr>
 *            <tr>
 *               <td>State</td>
 *               <td>No</td>
 *               <td>String</td>
 *               <td>255</td>
 *            </tr>
 *            <tr>
 *               <td>City</td>
 *               <td>No</td>
 *               <td>String</td>
 *               <td>255</td>
 *            </tr>
 *            <tr>
 *               <td>Zipcode</td>
 *               <td>No</td>
 *               <td>String</td>
 *               <td>255</td>
 *            </tr>
 *            <tr>
 *               <td>Phone</td>
 *               <td>No</td>
 *               <td>Number</td>
 *               <td>10</td>
 *            </tr>
 *            <tr>
 *               <td>Fax</td>
 *               <td>No</td>
 *               <td>Number</td>
 *               <td>10</td>
 *            </tr>
 *          </table>
 *          </p>
 *          <br/>
 *          <b>Note:</b> Active status is set to <b>TRUE</b> by default for new organization. Admin Portal in the current release do not allow to deactive an organization.
 *     </section>
 *     <section>
 *      <h4>Failure Status Codes</h3>
 *       <p>
 *       This section describes the Create/Edit/View organization status code information.
 *       <br>
 *       <table>
 *           <tr>
 *               <th>HTTP Status Code</th>
 *               <th>Service Error Code</th>
 *               <th>Error Message</th>
 *           </tr>
 *           <tr>
 *               <td>403</td>
 *               <td>OS_PERMISSION_DENIED</td>
 *               <td>You do not have permission to view this page.</td>
 *           </tr>
 *           <tr>
 *               <td>400</td>
 *               <td>OS_PARAM_DUPLICATE</td>
 *               <td>Duplicate name, <b>Name</b> for resource type Organization.</td>
 *           </tr>
 *           <tr>
 *               <td>500</td>
 *               <td>OS_INTERNAL_ERROR</td>
 *               <td>Internal Server Error</td>
 *           </tr>
 *           <tr>
 *               <td>503</td>
 *               <td></td>
 *               <td>Service Unavailable</td>
 *           </tr>
 *       </table>
 *   </p>
 *   </section
 *   <section>
 *      <h4>Dependent System settings, Platform services & Role Permission</h3>
 *      <p>This section describes the list of dependent system settings & platform services required for functioning of create/view/edit organization application page.</p>
 *       <h5>System Settings</h4>
 *       <p>Table lists all the configurable system setting(s) defined in configuration service with global/organization scope which will be used while editing & creating organiation</p>
 *       <br>
 *       <table>
 *           <tr>
 *               <th>Key</th>
 *               <th>Type</th>
 *               <th>Value</th>
 *               <th>Scope</th>
 *               <th>Category</th>
 *               <th>Mandatory</th>
 *           </tr>
 *           <tr>
 *               <td>solution.organization.types</td>
 *               <td>JSON</td>
 *               <td>[{"text":"Health System","coding":[{"code":"prov","system":"http://terminology.hl7.org/CodeSystem/organization-type","display":"Healthcare Provider","version":"0.1.0","userSelected":true}]},{"text":"Clinic","coding":[{"code":"dept","system":"http://terminology.hl7.org/CodeSystem/organization-type","display":"Hospital Department","version":"0.1.0","userSelected":true}]},{"text":"Standalone Clinic","coding":[{"code":"dept","system":"http://terminology.hl7.org/CodeSystem/organization-type","display":"Hospital Department","version":"0.1.0","userSelected":true}]},{"text":"Region","coding":[{"code":"region","system":"http://terminology.hl7.org/CodeSystem/organization-type","display":"Region","version":"0.1.0","userSelected":false}]},{"text":"Sponsor","coding":[{"code":"crs","system":"http://terminology.hl7.org/CodeSystem/organization-type","display":"Clinical Research Sponsor","version":"0.1.0","userSelected":false}]}]</td>
 *               <td>Global</td>
 *               <td>Solution_Config</td>
 *               <td>No</td>
 *           </tr>
 *           <tr>
 *               <td>solution.organization.creation.policy</td>
 *               <td>JSON</td>
 *               <td>{"Region":["Health System","Standalone Clinic"],"Sponsor":["Region"],"Health System":["Clinic"]}</td>
 *               <td>Global</td>
 *               <td>Solution_Config</td>
 *               <td>No</td>
 *           </tr>
 *           <tr>
 *               <td>solution.organization.invitation.policy</td>
 *               <td>JSON</td>
 *               <td>{"Clinic":[{"role":"Clinic Admin","isAdmin":true,"template":"ADMIN_Invitation","enrollments":[{"role":"Clinic Admin","owner":true,"parent":true}]}],"Sponsor":[{"role":"Sponsor Admin","isAdmin":true,"template":"ADMIN_Invitation"}],"Health System":[{"role":["Health System Customer Admin","CMO"],"isAdmin":true,"template":"ADMIN_Invitation"}],"Standalone Clinic":[{"role":["Clinic Customer Admin","CMO"],"isAdmin":true,"template":"ADMIN_Invitation"}]}</td>
 *               <td>Global</td>
 *               <td>Solution_Config</td>
 *               <td>No</td>
 *           </tr>
 *           <tr>
 *               <td>solution.organization.address.country.default.values</td>
 *               <td>JSON</td>
 *               <td>[{"code":"US","name":"United States","states":[{"code":"AL","name":"Alabama"},{"code":"AK","name":"Alaska"},{"code":"AS","name":"American Samoa"},{"code":"AZ","name":"Arizona"},{"code":"AR","name":"Arkansas"},{"code":"UM-81","name":"Baker Island"},{"code":"CA","name":"California"},{"code":"CO","name":"Colorado"},{"code":"CT","name":"Connecticut"},{"code":"DE","name":"Delaware"},{"code":"DC","name":"District of Columbia"},{"code":"FL","name":"Florida"},{"code":"GA","name":"Georgia"},{"code":"GU","name":"Guam"},{"code":"HI","name":"Hawaii"},{"code":"UM-84","name":"Howland Island"},{"code":"ID","name":"Idaho"},{"code":"IL","name":"Illinois"},{"code":"IN","name":"Indiana"},{"code":"IA","name":"Iowa"},{"code":"UM-86","name":"Jarvis Island"},{"code":"UM-67","name":"Johnston Atoll"},{"code":"KS","name":"Kansas"},{"code":"KY","name":"Kentucky"},{"code":"UM-89","name":"Kingman Reef"},{"code":"LA","name":"Louisiana"},{"code":"ME","name":"Maine"},{"code":"MD","name":"Maryland"},{"code":"MA","name":"Massachusetts"},{"code":"MI","name":"Michigan"},{"code":"UM-71","name":"Midway Atoll"},{"code":"MN","name":"Minnesota"},{"code":"MS","name":"Mississippi"},{"code":"MO","name":"Missouri"},{"code":"MT","name":"Montana"},{"code":"UM-76","name":"Navassa Island"},{"code":"NE","name":"Nebraska"},{"code":"NV","name":"Nevada"},{"code":"NH","name":"New Hampshire"},{"code":"NJ","name":"New Jersey"},{"code":"NM","name":"New Mexico"},{"code":"NY","name":"New York"},{"code":"NC","name":"North Carolina"},{"code":"ND","name":"North Dakota"},{"code":"MP","name":"Northern Mariana Islands"},{"code":"OH","name":"Ohio"},{"code":"OK","name":"Oklahoma"},{"code":"OR","name":"Oregon"},{"code":"UM-95","name":"Palmyra Atoll"},{"code":"PA","name":"Pennsylvania"},{"code":"PR","name":"Puerto Rico"},{"code":"RI","name":"Rhode Island"},{"code":"SC","name":"South Carolina"},{"code":"SD","name":"South Dakota"},{"code":"TN","name":"Tennessee"},{"code":"TX","name":"Texas"},{"code":"UM","name":"United States Minor Outlying Islands"},{"code":"VI","name":"United States Virgin Islands"},{"code":"UT","name":"Utah"},{"code":"VT","name":"Vermont"},{"code":"VA","name":"Virginia"},{"code":"UM-79","name":"Wake Island"},{"code":"WA","name":"Washington"},{"code":"WV","name":"West Virginia"},{"code":"WI","name":"Wisconsin"},{"code":"WY","name":"Wyoming"}],"countryCode":"+1"}]</td>
 *               <td>Global</td>
 *               <td>Solution_Config</td>
 *               <td>No</td>
 *           </tr>
 *           <tr>
 *               <td>solution.configuration.defaults</td>
 *               <td>JSON</td>
 *               <td>{"organization":{"defaultState":"California","defaultCountry":"United States","displayPattern":"${code} | ${name}","excludeDefaults":["Region"]}}</td>
 *               <td>Global</td>
 *               <td>Solution_Config</td>
 *               <td>No</td>
 *           </tr>
 *           <tr>
 *               <td>solution.organization.settings</td>
 *               <td>JSON</td>
 *               <td>{"overrideTemplates": false}</td>
 *               <td>Organization</td>
 *               <td>solution.organization.settings</td>
 *               <td>Yes</td>
 *           </tr>
 *          
 *       </table>
 *       <br>
 *      <h5>Platform Service(s)</h4>
 *      <p>Table lists all the dependent platform service(s) and thier specific version(s) for create/View/Edit organization.</p>
 *       <br>
 *       <table>
 *           <tr>
 *               <th>Service Name</th>
 *               <th>Version</th>
 *           </tr>
 *           <tr>
 *               <td>Organization Service</td>
 *               <td>1.3.1</td>
 *           </tr>
 *           <tr>
 *               <td>User Management Service</td>
 *               <td>1.6.0</td>
 *           </tr>
  *           <tr>
 *               <td>Configuration Service</td>
 *               <td>1.5.0</td>
 *           </tr>
 *           <tr>
 *               <td>Notification Service</td>
 *               <td>1.4.0</td>
 *           </tr>
 *       </table>
 *       <br>
 *      <h5>API Role Permission(s)</h4>
 *      <p>Table lists the required API role permissions for create/Edit/View organization.</p>
 *       <br>
 *       <table>
 *           <tr>
 *               <th>Feature</th>
 *               <th>API URL</th>
 *               <th>API Method</th>
 *               <th>API Permission</th>
 *               <th>Required</th>
 *           </tr>
 *           <tr>
 *               <td>View Organization</td>
 *               <td>/organizations/{id}</td>
 *               <td>GET</td>
 *               <td>organization-service.organization.get</td>
 *               <td>Yes</td>
 *           </tr>
 *           <tr>
 *               <td>View Organization</td>
 *               <td>/organizations/{organization-id}/settings</td>
 *               <td>GET</td>
 *               <td>configuration-service.organization-setting.list</td>
 *               <td>Yes</td>
 *           </tr>
 *           <tr>
 *               <td>View Organization</td>
 *               <td>/system-settings</td>
 *               <td>GET</td>
 *               <td>configuration-service.setting.list</td>
 *               <td>No</td>
 *           </tr>
 *           <tr>
 *               <td>Edit Organization</td>
 *               <td>/organizations/{id}</td>
 *               <td>GET</td>
 *               <td>organization-service.organization.get</td>
 *               <td>Yes</td>
 *           </tr>
 *           <tr>
 *               <td>Edit Organization</td>
 *               <td>/organizations/{id}</td>
 *               <td>PUT, PATCH</td>
 *               <td>organization-service.organization.update, organization-service.organization.patch</td>
 *               <td>Yes</td>
 *           </tr>
 *           <tr>
 *               <td>Edit Organization</td>
 *               <td>/organizations/{organization-id}/settings</td>
 *               <td>GET</td>
 *               <td>configuration-service.organization-setting.list</td>
 *               <td>Yes</td>
 *           </tr>
 *           <tr>
 *               <td>Edit Organization</td>
 *               <td>/system-settings</td>
 *               <td>GET</td>
 *               <td>configuration-service.setting.list</td>
 *               <td>No</td>
 *           </tr>
 *           <tr>
 *               <td>Create Organization</td>
 *               <td>/organizations</td>
 *               <td>POST</td>
 *               <td>organization-service.organization.create</td>
 *               <td>Yes</td>
 *           </tr>
 *           <tr>
 *               <td>Create Organization</td>
 *               <td>/system-settings</td>
 *               <td>GET</td>
 *               <td>configuration-service.setting.list</td>
 *               <td>Yes</td>
 *           </tr>
 *           <tr>
 *               <td>Create Organization</td>
 *               <td>/organizations/{organization-id}/settings</td>
 *               <td>GET</td>
 *               <td>configuration-service.organization-setting.list</td>
 *               <td>Yes</td>
 *           </tr>
 *           <tr>
 *               <td>Create Organization</td>
 *               <td>/organizations/{organization-id}/categories/{category-id}</td>
 *               <td>POST</td>
 *               <td>configuration-service.organization-category.create</td>
 *               <td>Yes</td>
 *           </tr>
 *           <tr>
 *               <td>Create Organization</td>
 *               <td>/organizations/{organization-id}/categories</td>
 *               <td>GET</td>
 *               <td>configuration-service.organization-category.list</td>
 *               <td>Yes</td>
 *           </tr>
 *           <tr>
 *               <td>Create Organization</td>
 *               <td>/organizations/{organization-id}/settings</td>
 *               <td>POST</td>
 *               <td>configuration-service.organization-setting.create</td>
 *               <td>Yes</td>
 *           </tr>
 *           <tr>
 *               <td>Create Organization</td>
 *               <td>/notifications/templates</td>
 *               <td>GET</td>
 *               <td>notification-service.template.list</td>
 *               <td>Yes</td>
 *           </tr>
 *           <tr>
 *               <td>Create Organization</td>
 *               <td>/notifications/templates</td>
 *               <td>POST</td>
 *               <td>notification-service.template.create</td>
 *               <td>Yes</td>
 *           </tr>
 *       </table>
 *   </section>
 *   <section>
 *    <p>Sequence Diagram for create create/view/edit organization</p>
 *   </section>
 */
const CreateOrganization = ({
  isNewChildOrganization = false,
  parentOrganizationId = ''
}: CreateOrganizationProps) => {
  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const toastData = useAppSelector((state: any) => state.toast);
  const storedURLPaths = useAppSelector((state: any) => state.organization.urlPaths);

  const userProfileInfo: any = useAppSelector((state: any) => state.userManagement.userProfileInfo);

  const [organizationCategories, setOrganizationCategories] = useState<Category[]>([]);
  const [userInvitePolicy, setUserInvitePolicy] = useState<SystemSetting>();
  const [organizationSetting, setOrganizationSetting] = useState<SystemSetting>();

  const [isLoading, setIsLoading] = useState(false);
  // const [adminUsers, setAdminUsers] = useState<any[]>([]);
  // const [organizationData, setOrganizationData] = useState<any>({});
  // const [parentOrganization, setParentOrganization] = useState<any>({});
  const [overrideTemplates, setOverrideTemplates] = useState<boolean>(false);

  const [organization, setOrganization] = useState<any>();

  const organizationTypes: any =
    useAppSelector((state: any) => state.configuration.organizationTypes) || [];
  const orgCreationPolicy: any = useAppSelector(
    (state: any) => state.configuration.orgCreationPolicy
  );
  const orgAddressCountryList: any = useAppSelector(
    (state: any) => state.configuration.orgAddressCountryList
  );
  const solutionConfigurationDefaults: any = useAppSelector(
    (state: any) => state.configuration.solutionConfigurationDefaults
  );

  const [countryFlag, setCountryFlag] = useState(
    solutionConfigurationDefaults.organization?.defaultCountry || ''
  );

  const [formData, setFormData] = useState<NewOrganization>({
    name: '',
    phone: '',
    fax: '',
    address: '',
    city: '',
    zip: '',
    state: '',
    country: '',
    type: '',
    countryCode: AppConstants.DEFAULT_COUNTRY_CODE,
    faxCountryCode: AppConstants.DEFAULT_COUNTRY_CODE
  });

  const [error, setError] = useState({
    name: '',
    phone: '',
    fax: '',
    address: '',
    city: '',
    zip: '',
    state: '',
    country: '',
    type: ''
  });

  const [initialFormData, setInitialFormData] = useState<NewOrganization>({
    name: '',
    phone: '',
    fax: '',
    address: '',
    city: '',
    zip: '',
    state: '',
    country: '',
    type: '',
    countryCode: '+',
    faxCountryCode: '+'
  });

  const [orgCreated, setOrgCreated] = useState(false);
  // const [orgIconFile, setOrgIconFile] = useState<any>();
  // const [tabIconFile, setTabIconFile] = useState<any>();

  const [taskTrackerOpen, setTaskTrackerOpen] = useState(false);
  const [taskList, setTaskList] = useState(TaskTrackerConstants.CREATE_ORGANIZATION_TASKS);

  const { t } = useTranslation();
  // const location = useLocation();
  const query = useQuery();

  const taskTrackerStatusUpdate = (step: number, status: string) => {
    var tempTaskList = [...taskList];
    tempTaskList[step - 1].status = status;
    setTaskList(tempTaskList);
  };

  const resetTaskTracker = async () => {
    setTaskList(
      taskList.map((task: any) => {
        return { ...task, status: '' };
      })
    );
  };

  useEffect(() => {
    const init = async () => {
      let createOrg = true;
      const organizationId = params?.id as string;
      let parentOrgId = query.get(AppConstants.SOLUTION_QUERY_PARAM_PARENT_ORG);

      // Edit Organization
      if (organizationId) {
        createOrg = false;
      } else {
        // New Organization
        const { organizationId: loginOrganizationId } = getLoggedInOrg(userProfileInfo);
        parentOrgId = parentOrgId || loginOrganizationId;
      }

      const orgId = createOrg ? parentOrgId : organizationId;
      fetchOrganization(orgId);
      getOrganizationSetting(orgId);
      if (createOrg) {
        getOrgCategories(parentOrgId);
        getUserInvitationPolicy(parentOrgId);
      }
    };
    init();
    resetTaskTracker();
  }, []);

  useEffect(() => {
    // Navigate to Organization Listing page on organization creation success
    if (orgCreated) {
      dispatch(
        setToastData({
          toastMessage: t('T_TOAST_ORGANIZATION_CREATE_SUCCESS'),
          isToastActive: true,
          toastType: 'success'
        })
      );
      navigate(RouteConstants.ROUTE_OS_ORGANIZATION_LISTING);
    }

    // Update the breadcrumb for New/Edit organization
    if (organization) {
      if (params?.id) {
        dispatch(
          setURLPaths([
            ...storedURLPaths,
            {
              key: `/organization/edit/${params.id}`,
              label: `${t('T_ORGANIZATION')} > ${organization?.name}`
            }
          ])
        );
        populateForm();
      } else {
        dispatch(
          setURLPaths([
            ...storedURLPaths,
            {
              key: `/organization/new`,
              label: `${organization?.name} > ${t('T_NEW_ORGANIZATION')}`
            }
          ])
        );
      }
    }
  }, [orgCreated, organization]);

  useEffect(() => {
    if (formData.country && formData.country !== countryFlag) {
      setCountryFlag(formData.country);
    }
  }, [formData]);

  const populateForm = async () => {
    if (organization) {
      const { name, address = [], telecom = [] } = organization;
      const [address1 = []] = address || [];
      const { line = [], city = '', country = '', postalCode: zip = '', state = '' } = address1;
      const [phone, fax] = telecom || [];

      const phoneNo = getCountryCodeAndNumber(phone?.value || '');
      const faxNo = getCountryCodeAndNumber(fax?.value || '');

      let countryEl;
      if (!isEmpty(orgAddressCountryList) && country) {
        countryEl =
          find(orgAddressCountryList, function (item: any) {
            return item.code === country || item.name === country;
          }) || {};
      }
      const countryCode = phone?.value ? phoneNo.countryCode : countryEl?.countryCode;
      const faxCountryCode = fax?.value ? faxNo.countryCode : countryEl?.countryCode;

      const formData = {
        name,
        phone: phoneNo.formattedNumber,
        fax: faxNo.formattedNumber,
        address: line.join(','),
        city,
        zip,
        state,
        country,
        type: getOrganizationType(organization),
        countryCode: countryCode,
        faxCountryCode: faxCountryCode
      };
      setFormData({ ...formData });
      setInitialFormData({ ...formData });
    }
  };

  const getUserInvitationPolicy = async (organizationId: string) => {
    const response = await getOrganizationSettings(
      organizationId,
      AppConstants.CATEGORY_USER_MGMT_INVITATION_POLICY,
      AppConstants.DEFAULT_PAGE,
      AppConstants.MAXIMUM_PAGE_SIZE
    );

    let toastMessage = '';
    const { status, data } = response;
    if (status == HTTP_STATUS.HTTP_OK) {
      const { data: invitationPolicys = [] } = data;
      if (invitationPolicys.length) {
        const [invitationPolicy] = invitationPolicys;
        setUserInvitePolicy(invitationPolicy);
        return;
      }
      toastMessage = `${t('T_TOAST_MSG_PART1')} ${
        AppConstants.CATEGORY_USER_MGMT_INVITATION_POLICY
      } ${t('T_TOAST_MSG_PART2')}`;
    } else {
      toastMessage = `${t('T_TOAST_ERR_MSG_PART1')} ${
        AppConstants.CATEGORY_USER_MGMT_INVITATION_POLICY
      } ${t('T_TOAST_ERR_MSG_PART2')}`;
    }
    dispatch(
      setToastData({
        toastMessage,
        isToastActive: true,
        toastType: 'error'
      })
    );
  };

  const getOrgCategories = async (organizationId: string) => {
    const response = await getCategories(
      organizationId,
      AppConstants.DEFAULT_PAGE,
      AppConstants.MAXIMUM_PAGE_SIZE
    );

    const { status, data: orgCategories = [] } = response;
    if (status == HTTP_STATUS.HTTP_OK) {
      const { data: orgCategoriesList = [] } = orgCategories;
      setOrganizationCategories(orgCategoriesList);
    }
  };

  const getOrganizationSetting = async (organizationId: string) => {
    const response = await getOrganizationSettings(
      organizationId,
      AppConstants.CATEGORY_ORGANIZATION_SETTINGS,
      AppConstants.DEFAULT_PAGE,
      AppConstants.MAXIMUM_PAGE_SIZE
    );
    const { status, data: settingResponse } = response;
    if (status == HTTP_STATUS.HTTP_OK) {
      const { data: systemSettings = [] } = settingResponse;
      const [organizationSetting] = systemSettings.filter(
        (setting: SystemSetting) => setting.key === AppConstants.CATEGORY_ORGANIZATION_SETTINGS
      );
      if (organizationSetting) {
        setOrganizationSetting(organizationSetting);
        const { value = {} } = organizationSetting;
        setOverrideTemplates(value.copyTemplates || overrideTemplates);
      }
    }
  };

  const fetchOrganization = async (id: string) => {
    setIsLoading(true);
    const response = await getOrganization(id);
    const { status, data } = response;
    if (status == HTTP_STATUS.HTTP_OK) {
      setOrganization(data);
    } else {
      dispatch(
        setToastData({
          toastMessage: getFailureMessage(response),
          isToastActive: true,
          toastType: 'error'
        })
      );
    }
    setIsLoading(false);
  };

  // const enrollPeerAdmins = async (organizationId: string) =>
  //   adminUsers &&
  //   adminUsers.length > 0 &&
  //   Promise.all(
  //     adminUsers.map((adminUser: any) => enrollUser(adminUser?.userId, [{ organizationId }]))
  //   );

  const validateFormData = (formData: NewOrganization) => {
    let errors: NewOrganization = {
      name: '',
      phone: '',
      fax: '',
      address: '',
      city: '',
      zip: '',
      state: '',
      country: '',
      type: ''
    };

    errors['name'] = validateName(formData.name.trim(), t);
    formData.phone
      ? (errors['phone'] = validateContact(formData.phone, true, t))
      : (errors['phone'] = '');
    formData.fax ? (errors['fax'] = validateContact(formData.fax, true, t)) : (errors['fax'] = '');

    // errors['fax'] = validateContact(formData.fax, false, t);
    errors['address'] = validateLength(formData.address.trim(), t, 1024);
    errors['city'] = validateLength(formData.city.trim(), t, 255);
    // fieldIsEmpty(formData.country)
    //   ? (errors['country'] = t('T_VALIDATION_ERR_EMPTY_MSG'))
    //   : (errors['country'] = '');
    // fieldIsEmpty(formData.zip)
    //   ? (errors['zip'] = t('T_VALIDATION_ERR_EMPTY_MSG'))
    //   : (errors['zip'] = '');
    // !fieldIsEmpty(formData.zip) && !validatePoastalCode(formData.zip)
    //   ? (errors['zip'] = t('T_VALIDATION_ERR_POST_CODE_MSG'))
    //   : (errors['zip'] = '');
    // fieldIsEmpty(formData.state)
    //   ? (errors['state'] = t('T_VALIDATION_ERR_EMPTY_MSG'))
    //   : (errors['state'] = '');
    setError({ ...errors });
    return errors;
  };

  const createPayload = (formData: NewOrganization) => {
    const { id, parentId } = organization;
    const { countryCode = '', faxCountryCode = '', type, phone = '', fax = '' } = formData;

    const orgPayload = {
      name: formData.name.trim(),
      type: isEmpty(orgCreationPolicy)
        ? type
          ? [{ ...customOrgTypeCoding(type) }]
          : []
        : organizationTypes?.filter((orgType: any) => orgType?.text === type),
      parentId: params?.id ? parentId : id,
      telecom: [
        {
          system: 'phone',
          value:
            (countryCode && phone) || (!countryCode && phone)
              ? `${countryCode ? (countryCode !== '+' ? countryCode : '') : ''}${phone.replace(
                  /\D/g,
                  ''
                )}`
              : '',
          rank: 1
        },
        {
          system: 'fax',
          value:
            (faxCountryCode && fax) || (!faxCountryCode && fax)
              ? `${
                  faxCountryCode ? (faxCountryCode !== '+' ? faxCountryCode : '') : ''
                }${fax.replace(/\D/g, '')}`
              : '',
          rank: 2
        }
      ],
      address: [
        {
          line: [formData?.address.trim()],
          city: formData?.city.trim() || '',
          state: formData?.state,
          country: formData?.country,
          postalCode: formData?.zip
        }
      ],
      contact: [
        {
          userId: userProfileInfo?.userId
        }
      ]
    };
    return orgPayload;
  };

  const displaySuccessToastMessage = (formData: NewOrganization) => {
    dispatch(
      setToastData({
        toastMessage: `${formData.name} ${t('T_UPDATE_SUCCESS_MSG')}`,
        isToastActive: true,
        toastType: 'success'
      })
    );
    // navigate(-2);
  };

  const displayErrorToastMessage = (toastMessage: string) => {
    dispatch(
      setToastData({
        toastMessage,
        isToastActive: true,
        toastType: 'error'
      })
    );
  };

  const displayToastMessage = (toastMessage: string, toastType: string = 'success') => {
    dispatch(
      setToastData({
        toastMessage,
        isToastActive: true,
        toastType
      })
    );
  };

  const updateOrganizationSettings = async (settings: any, organizationId: string) => {
    if (!Object.keys(settings).length) {
      return;
    }
    // TODO: Below lines never execute untill settings has passsed with empty object.
    // TODO: don't Delete this code =, it will be used in future but at this moment, this is useless.
    // let orgIconURL: string =
    //   orgIconFile && (await uploadImage(organizationId, orgIconFile, 'org', true));
    // let tabIconURL: string =
    //   tabIconFile && (await uploadImage(organizationId, tabIconFile, 'tab', true));
    //
    // if (orgIconURL || tabIconURL) {
    //   let switchOrganization = false;
    //   const loginOrgId = getLoggedInOrg().organizationId;
    //   if (params?.id !== loginOrgId) {
    //     switchOrganization = true;
    //   }
    //   if (switchOrganization) {
    //     await switchSession(organizationId);
    //   }
    //   await overrideOrganizationSettings(organizationId, orgIconURL, tabIconURL);
    //   if (switchOrganization) {
    //     await switchSession(loginOrgId);
    //   }
    // }
  };

  const updateOrganization = async (payload: any, organizationSettings: Array<any> = []) => {
    if (params?.id) {
      setIsLoading(true);
      const response = await patchOrganization(payload, params?.id);
      const { status, data } = response;

      let toastType = 'success';
      let msg = `${formData.name} ${t('T_UPDATE_SUCCESS_MSG')}`;
      if (status === HTTP_STATUS.HTTP_OK) {
        setOrganization(data);
        await Promise.all(
          organizationSettings.map((organizationSetting) =>
            updateOrganizationSettings(organizationSetting, params?.id as string)
          )
        );
      } else {
        toastType = 'error';
        msg = getFailureMessage(response);
      }
      displayToastMessage(msg, toastType);
      setIsLoading(false);
    }
  };

  const handleSave = async (formData: NewOrganization) => {
    const errors = validateFormData(formData);
    if (
      Object.keys(errors).length !==
      Object.keys(errors).filter((validateError) => {
        return errors[validateError as keyof NewOrganization] === '';
      }).length
    ) {
      return;
    }
    const payload = createPayload(formData);
    if (params?.id) {
      const organizationSettings: Array<any> = new Array();
      await updateOrganization(payload, organizationSettings);
    } else {
      if (overrideTemplates) {
        setTaskTrackerOpen(true);
      } else {
        setIsLoading(true);
      }
      //taskTrackerStatusUpdate(1, AppConstants.TASK_TRACKER_STATUS_INPROGRESS);

      let success: boolean = true;
      let msg: string = `${formData.name} ${t('T_ORG_CREATED_SUCCESS_MSG')}`;

      const orgResponse = await createOrganization(payload);
      const { status, data: newOrganization } = orgResponse;

      if (status == HTTP_STATUS.HTTP_CREATED) {
        const { id, name: orgName } = newOrganization;

        success = true;
        setOrgCreated(true);
        updateOrganizationSettings({}, id);
      } else {
        displayErrorToastMessage(getFailureMessage(orgResponse));
      }
    }
    setIsLoading(false);
    resetTaskTracker();
    setTaskTrackerOpen(false);
  };

  const getRecursiveOrgs = (orgType: string, orgList: Set<any>) => {
    let creationPolices: Array<string> = orgCreationPolicy[orgType] || [];
    if (!creationPolices.length) {
      return null;
    }
    for (let index = 0; index < creationPolices.length; index++) {
      const element = creationPolices[index];
      orgList.add(element);
      getRecursiveOrgs(element, orgList);
    }
  };

  const uploadImage = async (
    bucketName: string,
    file: File,
    prefix: string,
    publicObject: boolean = false
  ) => {
    let imageURI: string = '';
    const { name } = file;
    const payload = {
      filePath: `${AppConstants.DEFAULT_ASSETS_FOLDER_PATH}${prefix}_${name}`,
      expirationTime: AppConstants.GCS_MAX_EXPIRATION_TIME,
      bucketName,
      versioning: true,
      action: AppConstants.GCS_UPLOAD_ACTION,
      publicObject
    };
    const response = await createSignedURL(payload);
    const { status, data } = response;
    let { url, headers = {} } = data;
    if (status == HTTP_STATUS.HTTP_CREATED) {
      imageURI = url;
      // const body = new FormData();
      // body.append('file', file, name);
      const uploadPayload = {
        url,
        body: file,
        headers: { ...headers, [AppConstants.HEADER_CONTENT_TYPE]: file.type }
      };

      await uploadFile(
        uploadPayload,
        () => {},
        (err: string) => {
          console.error(err);
        }
      );
    }
    return imageURI;
  };

  const overrideOrganizationSettings = async (
    organizationId: string,
    orgSignedURL?: any,
    tabSignedURL?: any
  ): Promise<any> => {
    const payload = {
      ...organizationSetting,
      value: {
        ...organizationSetting?.value,
        overrideTemplates,
        copyTemplates: overrideTemplates
        // customizations: {
        //   logoURL: orgSignedURL ? orgSignedURL.split('?')[0] : '',
        //   tabURL: tabSignedURL ? tabSignedURL.split('?')[0] : ''
        // }
      },
      categories: organizationCategories
        .filter(
          (category: Category) => category?.name === AppConstants.CATEGORY_ORGANIZATION_SETTINGS
        )
        .map((category: Category) => category?.id)
    };
    return createOrganizationSetting(organizationId, payload);
  };

  const setupOrganization = async (tgtOrganization: any) => {
    const adminPortalCommTpl = AppConstants.ADMINPORTAL_COMMUNICATION_TEMPLATES;

    const getAllRootTemplates = async (organizationId: string): Promise<any> => {
      const settingsResponse: any = await getOrganizationSettings(
        organizationId,
        AppConstants.SOLN_COMMUNICATION_TEMPLATES_CATEGORY,
        //AppConstants.CATEGORY_APPLICATION_SYSTEM,
        AppConstants.DEFAULT_PAGE,
        AppConstants.MAXIMUM_PAGE_SIZE
      );
      let templateNameList: string[] = [];
      let defaultTemplates: Array<SystemSetting> = [];

      const { status, data: responseData = {} } = settingsResponse;
      if (status == HTTP_STATUS.HTTP_OK) {
        const { data: systemSettings = [] } = responseData;
        defaultTemplates = systemSettings.filter(
          (systemSetting: SystemSetting) =>
            systemSetting?.key === AppConstants.CATEGORY_COMMUNICATION_TEMPLATE
        );
        let { templates = [] } = defaultTemplates.length && defaultTemplates[0]?.value;
        templates = [...templates, ...adminPortalCommTpl];
        templates = uniqBy(templates, 'category');
        templateNameList = templates.map((template: any) => template?.category);
      }
      return {
        response: Array.from(new Set(templateNameList)),
        templates: defaultTemplates.length && defaultTemplates[0],
        status
      };
    };

    const getTemplates = async (categories: Array<string> = [], id: string): Promise<any> => {
      return new Promise(async (resolve) => {
        const templateResponse = await getNotificationTemplatesForOrg(
          id,
          AppConstants.DEFAULT_PAGE,
          AppConstants.MAXIMUM_PAGE_SIZE
        );
        let data: Array<NotificationTemplate> = [];
        const { status, data: response } = templateResponse;
        if (status == HTTP_STATUS.HTTP_OK) {
          const { data: templateList = [] } = response;
          data = templateList.filter(
            (template: any) => categories.indexOf(template?.category) != -1
          );
          resolve({ data, status: HTTP_STATUS.HTTP_OK });
          return;
        }
        resolve({ data: response, status });
      });
    };

    const createTemplates = async (templates: Array<NotificationTemplate> = []): Promise<any> => {
      const stdTemplates = templates.map((template: NotificationTemplate) => {
        const { channel: channel1, params } = template;

        params.forEach((param: any) => {
          param.channel = channel1;
          delete param.id;
        });
        delete template.orgId;
        delete template.id;
        delete template.channel;
        //delete template.configParams;

        template.orgId = id;
        return template;
      });

      return Promise.all(stdTemplates.map((template) => createNotificationTemplate(template)));
    };

    const addUserInvitationPolicySetting = async (organizationId: any): Promise<any> => {
      const orgSettingCategories: Array<Category> = organizationCategories.filter(
        (category: Category) => category?.name === AppConstants.CATEGORY_USER_MGMT_INVITATION_POLICY
      );
      if (orgSettingCategories.length) {
        const [orgSettingCategory] = orgSettingCategories;
        const { id: categoryId } = orgSettingCategory;
        return createOrganizationSetting(organizationId, userInvitePolicy);
      }
    };

    const verifyOrganizationCategories = async (params: Array<any> = []) => {
      if (!params.length) {
        return false;
      }
      const response = await getCategories(
        params[0],
        AppConstants.DEFAULT_PAGE,
        AppConstants.MAXIMUM_PAGE_SIZE
      );
      const { status, data: categoryList = [] } = response;
      if (status == HTTP_STATUS.HTTP_OK) {
        const expectedCategories = params[1] || [];
        const { data: categoryListData = [] } = categoryList;
        const actualCategories = categoryListData
          .filter((category: any) => expectedCategories.includes(category?.name))
          .map((category: any) => category?.name);
        return isEqual(expectedCategories.sort(), actualCategories.sort());
      }
      return false;
    };

    const verifyTemplates = async (templates: Array<NotificationTemplate> = []) => {
      const response = await getNotificationTemplates(
        '',
        AppConstants.DEFAULT_PAGE,
        AppConstants.MAXIMUM_PAGE_SIZE,
        AppConstants.DEFAULT_SORT_BY,
        AppConstants.DEFAULT_SORT_DESC,
        id,
        true
      );
      const { status, data: templateResponse } = response;
      if (status == HTTP_STATUS.HTTP_OK) {
        const { data: templateList = [] } = templateResponse;
        const srcTemplates = templates
          .map((template: NotificationTemplate) => template?.category)
          .sort();
        const tgtTemplates = templateList
          // .filter((template: NotificationTemplate) => template?.orgId === id)
          .map((template: NotificationTemplate) => template?.category)
          .sort();
        return isEqual(srcTemplates, tgtTemplates);
      }
      return false;
    };

    const verifyUserInvitationPolicy = async (params: Array<string> = []) => {
      if (!params.length) {
        return false;
      }
      const response = await getOrganizationSettings(
        params[0],
        AppConstants.CATEGORY_USER_MGMT_INVITATION_POLICY
      );
      const { status, data: responseData } = response;
      if (status == HTTP_STATUS.HTTP_OK) {
        const { data: settingList = [] } = responseData;
        if (settingList.length) {
          return isEqual(settingList[0]?.value, userInvitePolicy?.value);
        }
      }
      return false;
    };

    const VerifyOrganizationSettings = async (params: Array<string> = []) => {
      if (!params.length) {
        return false;
      }
      const response = await getOrganizationSettings(
        params[0],
        AppConstants.CATEGORY_ORGANIZATION_SETTINGS
      );
      const { status, data: responseData } = response;
      if (status == HTTP_STATUS.HTTP_OK) {
        const { data: settingList = [] } = responseData;
        if (settingList.length) {
          return isEqual(settingList[0]?.value?.overrideTemplates, overrideTemplates);
        }
      }
      return false;
    };

    const validationCheck = async (tasks: Array<any>) => {
      return tasks.map(async (task: any) => await task?.func([...task?.params]));
    };

    const addCategoriesToOrganization = (categories: Array<string> = []) => {
      return Promise.all(
        organizationCategories
          .filter((organizationCategory: any) => categories.includes(organizationCategory?.name))
          .map((category: any) => addCategoryToOrganization(id, category?.id))
      );
    };

    const { id: userOrg } = organization;
    const { id } = tgtOrganization;

    return new Promise(async (resolve) => {
      let success = false;
      taskTrackerStatusUpdate(2, AppConstants.TASK_TRACKER_STATUS_INPROGRESS);
      await addCategoriesToOrganization([
        AppConstants.CATEGORY_USER_MGMT_INVITATION_POLICY,
        //AppConstants.CATEGORY_APPLICATION_SYSTEM,
        AppConstants.CATEGORY_ORGANIZATION_SETTINGS
      ]);

      let copySuccess: boolean = false;
      let notificationTemplates: Array<NotificationTemplate> = new Array<NotificationTemplate>();
      if (overrideTemplates) {
        const { status, response: categories = [] } = await getAllRootTemplates(userOrg);
        if (status == HTTP_STATUS.HTTP_OK) {
          const response = await getTemplates(categories, userOrg);
          const { status, data = [] } = response;
          notificationTemplates = [...data];
          if (status == HTTP_STATUS.HTTP_OK) {
            await createTemplates(notificationTemplates);
            copySuccess = true;
          }
        }
      }

      taskTrackerStatusUpdate(
        2,
        overrideTemplates
          ? copySuccess
            ? AppConstants.TASK_TRACKER_STATUS_COMPLETE
            : AppConstants.TASK_TRACKER_STATUS_FAILED
          : AppConstants.TASK_TRACKER_STATUS_COMPLETE
      );
      taskTrackerStatusUpdate(3, AppConstants.TASK_TRACKER_STATUS_INPROGRESS);
      const results: Array<any> = await Promise.all(
        await validationCheck([
          {
            func: verifyOrganizationCategories,
            params: [
              id,
              [
                AppConstants.CATEGORY_USER_MGMT_INVITATION_POLICY,
                //AppConstants.CATEGORY_APPLICATION_SYSTEM,
                AppConstants.CATEGORY_ORGANIZATION_SETTINGS
              ]
            ]
          },
          { func: verifyTemplates, params: notificationTemplates }
          // { func: verifyUserInvitationPolicy, params: [id] }
          // { func: VerifyOrganizationSettings, params: [id] }
        ])
      );

      // await switchSession(id);
      await addUserInvitationPolicySetting(id);
      await overrideOrganizationSettings(id);
      // await switchSession(userOrg);

      success = !results.filter((result: any) => !result).length;
      success
        ? taskTrackerStatusUpdate(3, AppConstants.TASK_TRACKER_STATUS_COMPLETE)
        : taskTrackerStatusUpdate(3, AppConstants.TASK_TRACKER_STATUS_FAILED);
      resolve({ success });
    });
  };

  useEffect(() => {
    if (!isEmpty(solutionConfigurationDefaults)) {
      const { organization } = solutionConfigurationDefaults;
      const { defaultCountry = '', defaultState = '' } = organization;
      let countryEl, stateEl;

      if (!isEmpty(orgAddressCountryList) && defaultCountry) {
        countryEl =
          find(orgAddressCountryList, function (item: any) {
            return item.code === defaultCountry || item.name === defaultCountry;
          }) || {};

        if (!isEmpty(countryEl)) {
          stateEl =
            find(countryEl?.states, function (item: any) {
              return item.code === defaultState || item.name === defaultState;
            }) || {};
        }
      }

      const countryCode = countryEl?.countryCode || AppConstants.DEFAULT_COUNTRY_CODE;

      setFormData({
        ...formData,
        country: countryEl?.code || '',
        state: stateEl?.code || '',
        countryCode: countryCode,
        faxCountryCode: countryCode
      });
      setInitialFormData({
        ...initialFormData,
        country: countryEl?.code || '',
        state: stateEl?.code || '',
        countryCode: countryCode,
        faxCountryCode: countryCode
      });
    }
  }, [solutionConfigurationDefaults]);

  const getFormData = () => {
    let options: Array<any> = typesFiltered();

    const { organization } = solutionConfigurationDefaults;
    let {
      defaultCountry = '',
      displayPattern,
      defaultState = '',
      excludeDefaults = []
    } = organization || {};

    // if (excludeDefaults.includes(formData.type)) {
    //   defaultCountry = '';
    //   defaultState = '';
    // }

    let stateData =
      find(orgAddressCountryList, function (country: any) {
        return (
          country.code === (countryFlag || defaultCountry) ||
          country.name === (countryFlag || defaultCountry)
        );
      })?.states || [];

    return dynamicFormData().map((itemData) => {
      const { key } = itemData;
      switch (key) {
        case 'type':
          let itemObj = { ...itemData, disabled: params.id ? true : false };
          if (!options.length) {
            return {
              ...itemObj,
              type: 'text',
              tag: 'input',
              required: false
            };
          } else {
            return {
              ...itemObj,
              options
            };
          }
          break;
        case 'country':
        case 'state':
          return {
            ...itemData,
            required:
              isCountryStateRequired() || (formData['country'] !== '' && formData['state'] === ''),
            dynamicDisplayField: function (code: string, name: string) {
              return code || name ? parseTemplateLiteral(displayPattern, { code, name }) : '';
            },
            getCurrentSelection: function (value: string) {
              let element =
                key === 'country'
                  ? find(orgAddressCountryList, function (country: any) {
                      return country.code === value || country.name === value;
                    })
                  : find(stateData, function (state: any) {
                      return state.code === value || state.name === value;
                    });
              if (element) {
                let { code, name } = element;
                return parseTemplateLiteral(displayPattern, { code, name });
              } else {
                return '';
              }
            },
            options: key === 'country' ? orgAddressCountryList : stateData
          };
          break;
        case 'fax':
        case 'phone':
          if (!isEmpty(orgAddressCountryList)) {
            return {
              ...itemData,
              codeDisabled: formData['country'] !== ''
            };
          }
          break;
      }
      return { ...itemData };
    });
  };

  const isCountryStateRequired = () => {
    const { organization: organizationDefaults } = solutionConfigurationDefaults || {};
    const { excludeDefaults = [] } = organizationDefaults || {};
    const { type } = formData;

    if (excludeDefaults.length) {
      if (excludeDefaults.includes(type)) {
        return false;
      } else {
        return !isEmpty(orgAddressCountryList);
      }
    } else {
      return !isEmpty(orgAddressCountryList);
    }
  };

  const typesFiltered = () => {
    const { type = [] } = organization || {};
    const [coding] = type || [];
    if (coding) {
      const { text } = coding;
      if (!text) {
        return [];
      }
      return orgCreationPolicy[text.trim()] || [];
    } else {
      return [];
    }
  };

  const validateSaveButton = () => {
    let isSaveDisabled = false;
    if (
      formData['name'] === '' ||
      // formData['address'] === '' ||
      (!params.id &&
        typesFiltered().length &&
        formData['type'] === '' &&
        params.id !== userProfileInfo.createdByOrg) ||
      // formData['phone'] === '' ||
      (isCountryStateRequired() && (formData['country'] === '' || formData['state'] === '')) ||
      (!isCountryStateRequired() && formData['country'] !== '' && formData['state'] === '')

      // ||
      // formData['zip'] === '' ||
      // formData['city'] === ''
    ) {
      isSaveDisabled = true;
    }
    if (params.id) {
      const formValues: any = formData;
      let tempFormData: any = {};
      Object.keys(formValues).map((data: string) => {
        tempFormData = { ...tempFormData, [data]: formValues[data]?.trim() };
      });

      if (isEqual(tempFormData, initialFormData)) {
        isSaveDisabled = true;
      }
    }

    return isSaveDisabled;
  };

  const changeFormData = (formObject: any) => {
    let data = { ...formObject };
    let clonedFormData: any = { ...formData };
    const { organization: organizationDefaults } = solutionConfigurationDefaults || {};
    const { excludeDefaults = [], defaultCountry = '' } = organizationDefaults || {};
    let diff = Object.keys(data).filter((key: any) => data[key] !== clonedFormData[key]);
    let [changedKey = ''] = diff;

    if (changedKey == 'type' && excludeDefaults.includes(data[changedKey])) {
      let countryCode =
        find(orgAddressCountryList, function (item: any) {
          return item.code === defaultCountry || item.name === defaultCountry;
        })?.code || '';
      if (countryCode == data['country']) {
        data['country'] = '';
        data['state'] = '';
        data['countryCode'] = '+';
        data['faxCountryCode'] = '+';
      }
    }
    setFormData(data);
  };

  return (
    <LoaderContainer isLoading={isLoading}>
      <BrowserEventHandler>
        <div
          className="flex h-screen w-full flex-col  justify-start overflow-visible bg-gray-100 px-2 py-3"
          style={useTheme().theme.bgColorStyleForLoggedInPage}
        >
          <div className="flex">
            <div className="flex w-3/5">
              <div className="p-2">
                <Card
                  title={t('T_ORG_DETAIL_TITLE')}
                  icon={library}
                  alt={t('T_CARD_ALT_MESSAGE')}
                  height=""
                  width=""
                >
                  <MenuButtonsPortal>
                    <BiButton
                      className={'mr-2 text-primary'}
                      type="button"
                      onClick={() => navigate(-1)}
                    >
                      {t('T_CANCEL')}
                    </BiButton>
                    <BiButton
                      className={'bg-primary text-white'}
                      type="button"
                      onClick={() => {
                        handleSave(formData);
                      }}
                      disabled={validateSaveButton()}
                    >
                      {t('T_SAVE')}
                    </BiButton>
                  </MenuButtonsPortal>
                  <ToastNotification
                    message={toastData.toastMessage}
                    isActive={toastData.isToastActive}
                    setIsActive={() =>
                      dispatch(
                        setToastData({
                          toastMessage: '',
                          isToastActive: false,
                          toastType: null
                        })
                      )
                    }
                    status={toastData.toastType}
                  />
                  <div className="flex w-full flex-wrap">
                    <DynamicFormFieldRenderer
                      layout={NewOrganizationFormLayout.twoColumn}
                      dynamicFormData={getFormData()}
                      formData={formData}
                      setFormData={changeFormData}
                      formError={error}
                      editable={true}
                    />
                  </div>
                </Card>
              </div>
            </div>
          </div>

          {/* <div className="flex">
            <div className="w-3/5 p-2">
              <Card
                title={t('T_ORG_SETTINGS')}
                icon={SettingIcon}
                alt={t('T_CARD_ALT_MESSAGE')}
                height=""
                width=""
              >
                <div className={`mb-5 flex flex-row items-center`}>
                  <Checkbox
                    height="20px"
                    width="20px"
                    checked={overrideTemplates}
                    onChange={() => {
                      setOverrideTemplates(!overrideTemplates);
                    }}
                    disabled={params?.id ? true : false}
                  />
                  <span className="ml-2">{t('T_CLONE_DEFAULT_TEMPLATES')}</span>
                </div>
              </Card>
            </div>
          </div> */}

          <TaskTracker
            title={t('T_STEPS_INSTRUCTIONS_FOR_CREATE_ORG_MSG')}
            active={taskTrackerOpen}
            toggleVisiblity={() => {
              setTaskTrackerOpen(!taskTrackerOpen);
              setIsLoading(false);
              setTaskList(TaskTrackerConstants.CREATE_ORGANIZATION_TASKS);
              navigate(RouteConstants.ROUTE_OS_ORGANIZATION_LISTING);
            }}
            taskList={taskList}
          />
        </div>
      </BrowserEventHandler>
    </LoaderContainer>
  );
};

export default CreateOrganization;
